import { ClientMsgKind, Phase } from '@infiltre/shared';
import { useEffect } from 'react';
import { selectEliminatedThisRound, selectRoom, useAmIHost } from '@/entities/room';
import { RoundResult, resolveWinSound } from '@/features/game-round';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { trackEvent } from '@/shared/lib/analytics';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { useT } from '@/shared/lib/i18n';

export type OnlineRoundResultOrchestratorProps = {
  onFinished: () => void;
};

export const OnlineRoundResultOrchestrator = ({
  onFinished,
}: OnlineRoundResultOrchestratorProps) => {
  const t = useT();
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const driver = useSessionStore((s) => s.driver);
  const identity = useSessionStore((s) => s.identity);
  const isHost = useAmIHost();
  const eliminated = useGameStore(selectEliminatedThisRound);
  const winner = room?.winner ?? null;
  const finished = room?.phase === Phase.Finished;
  const eliminatedThisRound = room?.eliminatedThisRound ?? null;

  useEffect(() => {
    if (!room) return;
    if (finished && winner) {
      const cue = resolveWinSound(winner);
      if (cue) playSound(cue);
      return;
    }
    if (eliminatedThisRound) {
      playSound(SoundCue.PlayerEliminated);
      return;
    }
    playSound(SoundCue.RoundResult);
  }, [room, finished, winner, eliminatedThisRound, playSound]);

  if (!room || !driver || !identity) return null;
  const requested = room.nextRoundRequests.includes(identity.playerId);
  const total = room.nextRoundRequests.length;
  const needed = Math.ceil(room.players.length / 2);

  const continueLabel = (() => {
    if (finished && isHost) return t('round.next_round');
    if (finished) return t('round.replay.button');
    if (requested)
      return t('round.replay.waiting', { count: String(total), needed: String(needed) });
    return t('round.replay.button');
  })();

  const onContinue = () => {
    if (finished && isHost) {
      // Host-only rematch path: majority requests on Finished accumulate but never auto-advance.
      driver.send({ kind: ClientMsgKind.NextRound, actorPlayerId: identity.playerId });
      return;
    }
    if (requested) return;
    driver.send({
      kind: ClientMsgKind.RequestNextRound,
      actorPlayerId: identity.playerId,
    });
  };

  if (finished && !isHost && requested) {
    return (
      <RoundResult
        room={room}
        eliminated={eliminated}
        onContinue={() => {
          // Send LeaveRoom before WS teardown; 50ms gap mirrors LobbyRoomView.onBack.
          try {
            driver.send({ kind: ClientMsgKind.LeaveRoom, actorPlayerId: identity.playerId });
            trackEvent({
              name: 'lobby.left_voluntary',
              data: { roomId: room.id, phase: room.phase },
            });
          } catch {
            // socket already torn down — close path handles vacate
          }
          setTimeout(() => {
            void driver.stop();
            onFinished();
          }, 50);
        }}
        continueLabel={t('round.back_home')}
      />
    );
  }

  return (
    <RoundResult
      room={room}
      eliminated={eliminated}
      onContinue={onContinue}
      continueLabel={continueLabel}
    />
  );
};
